import React, { useCallback, useEffect, useState } from 'react'
import {
  Button,
  Checkbox,
  Form,
  Input,
  Modal,
  Popconfirm,
  Switch,
  Table,
  Tooltip,
  Typography
} from 'antd'
import { ColumnsType } from 'antd/es/table'
import {
  DeleteOutlined,
  EditOutlined,
  EyeOutlined,
  QuestionCircleOutlined
} from '@ant-design/icons'
import dayjs from 'dayjs'
import { sellerNoticesApi } from 'api/sellerNotices'
import { IAdminSellerNotice, ISellerNotice } from 'models/sellerNotices'
import { SellerNoticeModal } from 'components/SellerNoticeModal/SellerNoticeModal'
import { showMessage } from 'shared/utils/showMessage'
import { showServerError } from 'shared/utils/showServerError'
import styles from './SellerPopups.module.scss'

const TITLE_MAX_LENGTH = 200
const TEXT_MAX_LENGTH = 5000
const LINK_URL_MAX_LENGTH = 2000
const LINK_TEXT_MAX_LENGTH = 40
const DATE_FORMAT = 'DD.MM.YYYY'
const URL_PATTERN = /^https?:\/\/\S+$/i

interface IFormValues {
  title: string
  text: string
  is_active: boolean
  notify_new_sellers: boolean
  link_url: string
  link_text: string
}

type TPreview = Pick<ISellerNotice, 'title' | 'text' | 'link_url' | 'link_text'>

const getInitialValues = (notice: IAdminSellerNotice): IFormValues => ({
  title: notice.title,
  text: notice.text,
  is_active: notice.is_active,
  notify_new_sellers: notice.notify_new_sellers,
  link_url: notice.link_url ?? '',
  link_text: notice.link_text ?? ''
})

interface SellerPopupsProps {
  // Окна создаются на вкладке «Отправка» — список перечитываем при каждом открытии вкладки
  active: boolean
}

export const SellerPopups: React.FC<SellerPopupsProps> = ({ active }) => {
  const [form] = Form.useForm<IFormValues>()
  const [notices, setNotices] = useState<IAdminSellerNotice[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [editing, setEditing] = useState<IAdminSellerNotice | null>(null)
  const [isSaving, setIsSaving] = useState(false)
  const [preview, setPreview] = useState<TPreview | null>(null)

  const loadNotices = useCallback(async () => {
    try {
      const response = await sellerNoticesApi.getAll()
      setNotices(response.data)
    } catch (e) {
      showServerError(e)
    } finally {
      setIsLoading(false)
    }
  }, [])

  useEffect(() => {
    if (active) loadNotices()
  }, [active, loadNotices])

  const closeForm = () => setEditing(null)

  const handleSave = async (values: IFormValues) => {
    const data = {
      title: values.title.trim(),
      text: values.text.trim(),
      is_active: values.is_active,
      notify_new_sellers: values.notify_new_sellers,
      link_url: values.link_url?.trim() ?? '',
      link_text: values.link_text?.trim() ?? ''
    }

    if (!editing) return

    setIsSaving(true)
    try {
      await sellerNoticesApi.update(editing._id, data)
      showMessage('success', 'Окно сохранено', '')
      closeForm()
      await loadNotices()
    } catch (e) {
      showServerError(e)
    } finally {
      setIsSaving(false)
    }
  }

  const toggleActive = async (
    notice: IAdminSellerNotice,
    is_active: boolean
  ) => {
    try {
      await sellerNoticesApi.update(notice._id, { is_active })
      setNotices((prev) =>
        prev.map((item) =>
          item._id === notice._id ? { ...item, is_active } : item
        )
      )
    } catch (e) {
      showServerError(e)
    }
  }

  const removeNotice = async (notice: IAdminSellerNotice) => {
    try {
      await sellerNoticesApi.remove(notice._id)
      setNotices((prev) => prev.filter((item) => item._id !== notice._id))
      showMessage('success', 'Окно удалено', '')
    } catch (e) {
      showServerError(e)
    }
  }

  const showFormPreview = () => {
    const title = form.getFieldValue('title')?.trim()
    const text = form.getFieldValue('text')?.trim()
    if (!title || !text) {
      showMessage('error', 'Заполните заголовок и текст', '')
      return
    }
    const linkUrl = form.getFieldValue('link_url')?.trim()
    setPreview({
      title,
      text,
      link_url: URL_PATTERN.test(linkUrl ?? '') ? linkUrl : null,
      link_text: form.getFieldValue('link_text')?.trim() || null
    })
  }

  // При открытой форме предпросмотр рендерится внутри её модалки — antd тогда поднимает его выше формы
  const previewModal = (
    <SellerNoticeModal notice={preview} onClose={() => setPreview(null)} />
  )

  const columns: ColumnsType<IAdminSellerNotice> = [
    {
      title: 'Окно',
      dataIndex: 'title',
      render: (_, notice) => (
        <div className={styles.noticeCell}>
          <Typography.Text strong>{notice.title}</Typography.Text>
          <Typography.Paragraph
            type='secondary'
            ellipsis={{ rows: 2 }}
            className={styles.noticeText}
          >
            {notice.text}
          </Typography.Paragraph>
          <Typography.Text type='secondary' className={styles.noticeMeta}>
            Создано {dayjs(notice.createdAt).format(DATE_FORMAT)} ·{' '}
            {notice.notify_new_sellers
              ? 'показывается и новым селлерам'
              : 'новым селлерам не показывается'}
          </Typography.Text>
        </div>
      )
    },
    {
      title: (
        <span className={styles.titleWithHint}>
          Скрыли
          <Tooltip title='Сколько продавцов отметили «Не показывать снова» — из всех, кому окно показывается: одобренных продавцов, а без «Уведомлять новых селлеров» — только зарегистрированных до его создания. Остальные видят его при каждом входе в кабинет.'>
            <QuestionCircleOutlined className={styles.hint} />
          </Tooltip>
        </span>
      ),
      dataIndex: 'hidden_count',
      render: (_, notice) =>
        `${notice.hidden_count} из ${notice.audience_count}`
    },
    {
      title: 'Показывать',
      dataIndex: 'is_active',
      render: (_, notice) => (
        <Switch
          checked={notice.is_active}
          onChange={(checked) => toggleActive(notice, checked)}
        />
      )
    },
    {
      key: 'actions',
      render: (_, notice) => (
        <div className={styles.actions}>
          <Tooltip title='Как увидит продавец'>
            <Button icon={<EyeOutlined />} onClick={() => setPreview(notice)} />
          </Tooltip>
          <Tooltip title='Изменить'>
            <Button
              icon={<EditOutlined />}
              onClick={() => setEditing(notice)}
            />
          </Tooltip>
          <Popconfirm
            title='Удалить окно?'
            description='Продавцы, которые его ещё не скрыли, больше его не увидят'
            okText='Удалить'
            cancelText='Отмена'
            onConfirm={() => removeNotice(notice)}
          >
            <Button danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </div>
      )
    }
  ]

  return (
    <div className={styles.root}>
      <Table
        rowKey='_id'
        columns={columns}
        dataSource={notices}
        loading={isLoading}
        pagination={false}
        scroll={{ x: 720 }}
        locale={{
          emptyText:
            'Окон пока нет. Чтобы создать, отметьте «Всплывающее окно» на вкладке «Отправка»'
        }}
      />

      <Modal
        open={editing !== null}
        title={
          <span className={styles.titleWithHint}>
            Изменить окно
            <Tooltip title='Продавцы, которые уже скрыли окно, правку не увидят. Чтобы сообщить новое всем, отправьте новое окно на вкладке «Отправка».'>
              <QuestionCircleOutlined className={styles.hint} />
            </Tooltip>
          </span>
        }
        width={640}
        onCancel={closeForm}
        destroyOnClose
        footer={[
          <Button
            key='preview'
            icon={<EyeOutlined />}
            onClick={showFormPreview}
          >
            Предпросмотр
          </Button>,
          <Button key='cancel' onClick={closeForm}>
            Отмена
          </Button>,
          <Button
            key='save'
            type='primary'
            loading={isSaving}
            onClick={form.submit}
          >
            Сохранить
          </Button>
        ]}
      >
        {editing && (
          <>
            {/* Без clearOnDestroy общий экземпляр формы при следующем открытии
                подставил бы значения прошлого окна вместо initialValues */}
            <Form
              form={form}
              layout='vertical'
              onFinish={handleSave}
              initialValues={getInitialValues(editing)}
              clearOnDestroy
            >
              <Form.Item
                name='title'
                label='Заголовок'
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Заполните заголовок'
                  }
                ]}
              >
                <Input maxLength={TITLE_MAX_LENGTH} showCount />
              </Form.Item>
              <Form.Item
                name='text'
                label='Текст'
                rules={[
                  {
                    required: true,
                    whitespace: true,
                    message: 'Заполните текст'
                  }
                ]}
              >
                <Input.TextArea
                  maxLength={TEXT_MAX_LENGTH}
                  showCount
                  autoSize={{ minRows: 4, maxRows: 14 }}
                />
              </Form.Item>
              <Form.Item
                name='link_url'
                label={
                  <span className={styles.titleWithHint}>
                    Ссылка кнопки
                    <Tooltip title='Кнопка рядом с «OK» открывает ссылку в новой вкладке. Пустое поле — кнопки нет.'>
                      <QuestionCircleOutlined className={styles.hint} />
                    </Tooltip>
                  </span>
                }
                rules={[
                  {
                    pattern: URL_PATTERN,
                    message: 'Ссылка должна начинаться с http:// или https://'
                  }
                ]}
              >
                <Input
                  maxLength={LINK_URL_MAX_LENGTH}
                  placeholder='https://horsesmart.store/...'
                />
              </Form.Item>
              <Form.Item
                name='link_text'
                label={
                  <span className={styles.titleWithHint}>
                    Текст кнопки
                    <Tooltip title='Без текста на кнопке будет «Подробнее»'>
                      <QuestionCircleOutlined className={styles.hint} />
                    </Tooltip>
                  </span>
                }
              >
                <Input maxLength={LINK_TEXT_MAX_LENGTH} showCount />
              </Form.Item>
              <div className={styles.newSellersRow}>
                <Form.Item
                  name='notify_new_sellers'
                  valuePropName='checked'
                  noStyle
                >
                  <Checkbox>Уведомлять новых селлеров</Checkbox>
                </Form.Item>
                <Tooltip title='Без галочки окно увидят только продавцы, зарегистрированные до его создания, — чтобы новым продавцам не приходили сообщения о прошлых изменениях. С галочкой его увидят и те, кто зарегистрируется позже.'>
                  <QuestionCircleOutlined className={styles.hint} />
                </Tooltip>
              </div>
              <Form.Item
                name='is_active'
                valuePropName='checked'
                label='Показывать продавцам'
              >
                <Switch />
              </Form.Item>
            </Form>
            {previewModal}
          </>
        )}
      </Modal>

      {!editing && previewModal}
    </div>
  )
}
